import {
  BUFFER_LENGTH,
  OVERHEAT_FRAMES,
  PLOT_FPS,
  PlotSyntaxError,
  instrument,
  type Env,
} from '@coderaw/runtime'
import type { WorkerLike, WorkerMessage } from './protocol'
import { createWorkerSupervisor, type WorkerSupervisor } from './workerSupervisor'

export type RunnerState =
  'idle' | 'loading' | 'running' | 'syntax-error' | 'runtime-error' | 'overheated' | 'crashed'

export type RunnerError = { readonly message: string; readonly line?: number }

export type Timers = {
  readonly setInterval: (fn: () => void, ms: number) => unknown
  readonly clearInterval: (handle: unknown) => void
  readonly setTimeout: (fn: () => void, ms: number) => unknown
  readonly clearTimeout: (handle: unknown) => void
}

export type RunnerOptions = {
  readonly createWorker: () => WorkerLike
  readonly plotId: string
  /** Town time in seconds and the frame index it maps to. */
  readonly clock: () => { t: number; frame: number }
  readonly env: () => Env
  readonly fps?: number
  readonly watchdogMs?: number
  readonly loadTimeoutMs?: number
  readonly timers?: Timers
}

export type PlotRunner = {
  readonly load: (code: string) => void
  readonly start: () => void
  readonly stop: () => void
  readonly dispose: () => void
  readonly state: () => RunnerState
  readonly error: () => RunnerError | null
  readonly lastFrame: () => Uint8Array | null
  readonly onState: (cb: (state: RunnerState, error: RunnerError | null) => void) => () => void
  readonly onFrame: (cb: (buffer: Uint8Array) => void) => () => void
}

/** A frame that takes longer than this is a hung worker. */
export const WATCHDOG_MS = 250
/** Compiling is heavier than a frame, so a load gets a longer leash. */
export const LOAD_TIMEOUT_MS = 1000

const DEFAULT_TIMERS: Timers = {
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (h) => clearInterval(h as number),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as number),
}

type Listeners<T extends unknown[]> = Set<(...args: T) => void>
type Pending = { readonly id: number; readonly timer: unknown }

/** Mutable runner state shared by the small functions below. */
type Core = {
  readonly options: RunnerOptions
  readonly timers: Timers
  readonly stateListeners: Listeners<[RunnerState, RunnerError | null]>
  readonly frameListeners: Listeners<[Uint8Array]>
  readonly workers: WorkerSupervisor
  state: RunnerState
  error: RunnerError | null
  lastFrame: Uint8Array | null
  currentCode: string | null
  /** Code that hung twice in a row; refused until it changes. */
  crashedCode: string | null
  /** Code that hung once; a second hang latches it. */
  restartedCode: string | null
  pendingLoad: Pending | null
  pendingFrame: Pending | null
  overheatStreak: number
  nextId: number
  ticker: unknown
  disposed: boolean
}

const isPlotBuffer = (value: unknown): value is Uint8Array =>
  value instanceof Uint8Array && value.length === BUFFER_LENGTH

/** Parses on the host first so broken code never reaches the worker. */
const parseProblem = (code: string): RunnerError | null => {
  try {
    instrument(code)
    return null
  } catch (error) {
    if (error instanceof PlotSyntaxError) return { message: error.message, line: error.line }
    return { message: error instanceof Error ? error.message : String(error) }
  }
}

const subscribe =
  <T extends unknown[]>(set: Listeners<T>) =>
  (cb: (...args: T) => void) => {
    set.add(cb)
    return () => set.delete(cb)
  }

const setState = (core: Core, next: RunnerState, error: RunnerError | null = null): void => {
  core.state = next
  core.error = error
  for (const cb of core.stateListeners) cb(next, error)
}

const clearPending = (core: Core, which: 'pendingLoad' | 'pendingFrame'): void => {
  const pending = core[which]
  if (!pending) return
  core.timers.clearTimeout(pending.timer)
  core[which] = null
}

const clearAllPending = (core: Core): void => {
  clearPending(core, 'pendingLoad')
  clearPending(core, 'pendingFrame')
}

/** Final: the worker is gone and this code is refused until it changes. */
const crash = (core: Core, message: string): void => {
  clearAllPending(core)
  core.workers.terminate()
  core.crashedCode = core.currentCode
  setState(core, 'crashed', { message })
}

/**
 * A hung frame or load. The first time, restart the worker and reload the same
 * code quietly, since a main-thread stall can trip the watchdog on innocent code.
 * The second time in a row, crash and latch.
 */
const hang = (core: Core, message: string): void => {
  if (core.restartedCode === core.currentCode) {
    crash(core, message)
    return
  }
  clearAllPending(core)
  core.workers.terminate()
  core.restartedCode = core.currentCode
  if (core.currentCode !== null) postLoad(core, core.currentCode)
}

const postLoad = (core: Core, code: string): void => {
  clearAllPending(core)
  const loadId = core.nextId++
  const timer = core.timers.setTimeout(
    () => hang(core, 'loading took too long'),
    core.options.loadTimeoutMs ?? LOAD_TIMEOUT_MS,
  )
  core.pendingLoad = { id: loadId, timer }
  setState(core, 'loading')
  core.workers.post({ type: 'load', loadId, code, plotId: core.options.plotId })
}

const load = (core: Core, code: string): void => {
  if (core.disposed || code === core.crashedCode) return
  core.crashedCode = null
  if (code !== core.currentCode) core.restartedCode = null
  core.currentCode = code
  const problem = parseProblem(code)
  if (problem) {
    // Drop anything in flight so a late frame cannot repaint over the scaffolding.
    clearAllPending(core)
    setState(core, 'syntax-error', problem)
    return
  }
  postLoad(core, code)
}

const onLoadReply = (core: Core, message: Extract<WorkerMessage, { loadId: number }>): void => {
  if (!core.pendingLoad || message.loadId !== core.pendingLoad.id) return
  clearPending(core, 'pendingLoad')
  if (message.type === 'loaded') {
    core.overheatStreak = 0
    setState(core, 'running')
    return
  }
  const { message: text, line } = message
  setState(core, 'syntax-error', line === undefined ? { message: text } : { message: text, line })
}

const onFrameReply = (core: Core, message: Extract<WorkerMessage, { id: number }>): void => {
  if (!core.pendingFrame || message.id !== core.pendingFrame.id) return
  clearPending(core, 'pendingFrame')
  if (message.type === 'frame-error') {
    if (message.kind === 'runtime') {
      setState(core, 'runtime-error', { message: String(message.message) })
      return
    }
    core.overheatStreak += 1
    if (core.overheatStreak >= OVERHEAT_FRAMES) {
      setState(core, 'overheated', { message: String(message.message) })
    }
    return
  }
  // The spec: the only thing that comes out of the sandbox is the pixel buffer,
  // and the host checks its size and type. Anything else is treated as a crash.
  if (!isPlotBuffer(message.buffer)) {
    crash(core, 'the plot worker returned a malformed frame')
    return
  }
  core.overheatStreak = 0
  core.restartedCode = null // a good frame clears the hang strike
  core.lastFrame = message.buffer
  for (const cb of core.frameListeners) cb(message.buffer)
}

const handleMessage = (core: Core, message: WorkerMessage): void => {
  if (message.type === 'loaded' || message.type === 'load-error') onLoadReply(core, message)
  else onFrameReply(core, message)
}

const requestFrame = (core: Core): void => {
  if (core.state !== 'running' || core.pendingFrame || !core.workers.alive()) return
  const id = core.nextId++
  const { t, frame } = core.options.clock()
  const timer = core.timers.setTimeout(
    () => hang(core, 'a frame took too long'),
    core.options.watchdogMs ?? WATCHDOG_MS,
  )
  core.pendingFrame = { id, timer }
  core.workers.post({ type: 'frame', id, t, frame, env: core.options.env() })
}

const start = (core: Core): void => {
  if (core.ticker !== null || core.disposed) return
  const fps = core.options.fps ?? PLOT_FPS
  core.ticker = core.timers.setInterval(() => requestFrame(core), 1000 / fps)
}

const stop = (core: Core): void => {
  if (core.ticker === null) return
  core.timers.clearInterval(core.ticker)
  core.ticker = null
}

const dispose = (core: Core): void => {
  core.disposed = true
  stop(core)
  clearAllPending(core)
  core.workers.terminate()
  core.stateListeners.clear()
  core.frameListeners.clear()
}

const createCore = (options: RunnerOptions): Core => {
  // The supervisor needs the core for its callbacks and the core needs the
  // supervisor, so the callbacks close over the binding assigned just below.
  // eslint-disable-next-line prefer-const
  let core: Core
  const workers = createWorkerSupervisor({
    createWorker: options.createWorker,
    onMessage: (message) => handleMessage(core, message),
    onError: () => crash(core, 'the plot worker threw'),
  })
  core = {
    options,
    timers: options.timers ?? DEFAULT_TIMERS,
    stateListeners: new Set(),
    frameListeners: new Set(),
    workers,
    state: 'idle',
    error: null,
    lastFrame: null,
    currentCode: null,
    crashedCode: null,
    restartedCode: null,
    pendingLoad: null,
    pendingFrame: null,
    overheatStreak: 0,
    nextId: 1,
    ticker: null,
    disposed: false,
  }
  return core
}

/**
 * Drives one plot in a worker: loads code, requests frames at PLOT_FPS,
 * keeps the last good frame, and restarts the worker if a frame hangs.
 */
export const createPlotRunner = (options: RunnerOptions): PlotRunner => {
  const core = createCore(options)
  return {
    load: (code) => load(core, code),
    start: () => start(core),
    stop: () => stop(core),
    dispose: () => dispose(core),
    state: () => core.state,
    error: () => core.error,
    lastFrame: () => core.lastFrame,
    onState: subscribe(core.stateListeners),
    onFrame: subscribe(core.frameListeners),
  }
}

/** The production worker factory. Vite bundles the worker from this URL. */
export const createPlotWorker = (): WorkerLike =>
  new Worker(new URL('./plot.worker.ts', import.meta.url), { type: 'module' }) as WorkerLike
